import type * as React from "react"
import { CopyButton } from "@/components/app/copy-button"
import { cn } from "@/lib/utils"

/**
 * Monospace block for logs, JSON and snippets with a copy button.
 * For syntax-highlighted code inside prose, use <Markdown> with a fenced block.
 */
export function CodeBlock({
  code,
  title,
  language,
  copyable = true,
  wrap = false,
  maxHeight,
  className,
  actions,
}: {
  code: string
  title?: React.ReactNode
  language?: string
  copyable?: boolean
  wrap?: boolean
  maxHeight?: number | string
  className?: string
  actions?: React.ReactNode
}) {
  const showHeader = title || language || copyable || actions
  return (
    <div data-slot="code-block" className={cn("bg-muted/40 overflow-hidden rounded-lg border", className)}>
      {showHeader && (
        <div className="flex h-9 items-center justify-between gap-2 border-b pr-1 pl-3">
          <span className="text-muted-foreground truncate font-mono text-xs">{title ?? language}</span>
          <div className="flex items-center gap-0.5">
            {actions}
            {copyable && <CopyButton value={code} size="icon-xs" />}
          </div>
        </div>
      )}
      <pre
        className={cn(
          "overflow-auto p-3 font-mono text-[0.8rem] leading-relaxed",
          wrap ? "break-words whitespace-pre-wrap" : "whitespace-pre",
        )}
        style={maxHeight ? { maxHeight } : undefined}
      >
        <code>{code}</code>
      </pre>
    </div>
  )
}
